import { Link } from 'react-router-dom';
import { ArrowRight, Target, Layers, Search, Monitor } from 'lucide-react';

const ReferencesPage = () => {
  const categories = [
    {
      id: 1,
      icon: Target,
      title: 'Pilotage de programme',
      references: [
        {
          name: '[Plan ONE] Programme de transformation stratégique',
          client: 'Groupe d\'assurance',
          duration: '1 an · 12 axes stratégiques · 50 participants',
          context: 'Fédérer les filiales du groupe en un groupe unifié via un programme de transformation complet.',
          deliverables: [
            'Cadrage de 12 chantiers de transformation',
            'Mise en place de la gouvernance globale',
            'Déploiement de l\'outil Wrike',
            'Introduction des méthodologies agiles',
            'Coordination de 50 participants'
          ]
        },
        {
          name: '[Carve Out] Programme de scission d\'un opérateur funéraire',
          client: 'Opérateur de services funéraires',
          duration: '9-10 mois · 50 participants · 10 responsables métier',
          context: 'Scinder les activités en sociétés indépendantes avec analyse des impacts multidimensionnels.',
          deliverables: [
            'Analyse des impacts de la scission',
            'Mise en place de la gouvernance',
            'Animation des comités hebdomadaires',
            'Reporting COMEX et coordination des workstreams'
          ]
        }
      ]
    },
    {
      id: 2,
      icon: Layers,
      title: 'Design de modèle opérationnel',
      references: [
        {
          name: 'Définition du TOM d\'une BU de 500 personnes',
          client: 'Entreprise d\'expertises en assurance',
          duration: '9 mois · 1 consultant · CA 50M€',
          context: 'Améliorer la rentabilité, la satisfaction clients et la rétention des collaborateurs.',
          deliverables: [
            'Entretiens de diagnostic avec 40 managers',
            'Animation d\'ateliers de partage',
            'Co-construction du modèle cible via 6 workstreams',
            'Synthèse intégrant rentabilité, satisfaction et engagement'
          ]
        },
        {
          name: 'TOM Agile à l\'échelle pour DSI bancaire',
          client: 'Banque',
          duration: '9 mois · 4 consultants · 2 000 ETP',
          context: 'Déployer un modèle organisationnel agile pour accélérer les cycles de delivery.',
          deliverables: [
            'Réalisation de 80 entretiens diagnostic',
            'Management d\'équipe projet de 4 consultants',
            'Définition du modèle opérationnel cible',
            'Élaboration de la trajectoire de transformation'
          ]
        },
        {
          name: 'Design et déploiement d\'un TOM agile pour une Scale Up',
          client: 'Scale Up dans le digital',
          duration: 'Mission en cours',
          context: 'Pilotage de la mise en place d\'un nouveau modèle opérationnel pour supporter la croissance du groupe.',
          deliverables: [
            'État des lieux des points de douleurs (manque de partage de la vision, rôles à clarifier…)',
            'Réalisation d\'un séminaire d\'alignement (partage de l\'état des lieux, travail sur la vision et sur le modèle opérationnel cible)',
            'Mise en place de la gouvernance (C level - CFO, COO, CPO)',
            'Définition et mise en place des programmes transverses dans un modèle cible'
          ]
        }
      ]
    },
    {
      id: 3,
      icon: Search,
      title: 'Aide au choix / RFP & RFQ',
      references: [
        {
          name: 'Pilotage de l\'appel d\'offres DSI',
          client: 'Opérateur national de réseau électrique',
          duration: '18 mois · Budget 200M€ · 3 consultants',
          context: 'Rationaliser les prestataires et réduire les coûts d\'achat sur 5 ans.',
          deliverables: [
            'Cadrage global de l\'appel d\'offres',
            'Recueil des besoins conseil, MOA, Agile, TMA',
            'Rédaction des documents RFP/RFQ',
            'Assistance à la décision finale de sélection'
          ]
        }
      ]
    },
    {
      id: 4,
      icon: Monitor,
      title: 'Transformation digitale, ERP, SaaS',
      references: [
        {
          name: '[Déploiement Salesforce] Mise en œuvre du CRM',
          client: 'Groupe d\'assurance',
          duration: '9 mois · 1 consultant · 1 intégrateur · 100 utilisateurs',
          context: 'Unifier le système d\'information des 4 filiales du groupe.',
          deliverables: [
            'Définition du processus "Lead to Cash"',
            'Rédaction du document d\'expression des besoins',
            'Management de l\'équipe intégrateur (4 ETP)',
            'Pilotage de la formation de 100 utilisateurs'
          ]
        },
        {
          name: '[e-facturation] Déploiement d\'une solution de facturation électronique et revue du process P2P',
          client: 'Entreprise de services',
          duration: 'Mission récente',
          context: 'Mise en conformité avec la réforme de la facturation électronique et optimisation du processus Procure-to-Pay.',
          deliverables: [
            'Audit du process existant',
            'Identification des points de douleurs',
            'Design de la cible',
            'Migration des données',
            'Déploiement de la solution (accompagnement des utilisateurs)'
          ]
        }
      ]
    }
  ];

  return (
    <div data-testid="references-page" className="pt-20">
      {/* Hero */}
      <section className="section-padding bg-slate-50">
        <div className="container-main">
          <div className="max-w-3xl">
            <p className="caption-text text-[#BFA15F] mb-4">Références</p>
            <h1 className="heading-xl mb-6">
              Principales références<br />
              <span className="text-slate-400">récentes</span>
            </h1>
            <div className="divider mb-8" />
            <p className="body-large">
              Les références suivantes couvrent les secteurs de l'assurance, de la finance, 
              de l'énergie et des services, et témoignent de ma capacité à délivrer des 
              résultats dans des missions de transformation de toutes tailles et complexités.
            </p>
          </div>
        </div>
      </section>

      {/* References by Category */}
      <section data-testid="references-categories-section" className="section-padding bg-white">
        <div className="container-main">
          <div className="space-y-16">
            {categories.map((category) => (
              <div key={category.id} className="border-t border-slate-200 pt-12 first:border-0 first:pt-0">
                {/* Category Header */}
                <div className="flex items-center gap-4 mb-8">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#0a1628] text-white">
                    <category.icon size={24} strokeWidth={1.5} />
                  </div>
                  <h2 className="font-heading font-bold text-2xl text-[#0a1628]">
                    {category.id}. {category.title}
                  </h2>
                </div>

                {/* References Grid */}
                <div className="grid md:grid-cols-2 gap-6">
                  {category.references.map((ref, idx) => (
                    <div 
                      key={idx} 
                      className="bg-white border border-slate-100 hover:border-[#0a1628] hover:shadow-lg transition-all duration-300"
                    >
                      {/* Reference Header */}
                      <div className="p-6 border-b border-slate-100 bg-slate-50">
                        <h3 className="font-heading font-semibold text-lg text-[#0a1628] mb-2">
                          {ref.name}
                        </h3>
                        <div className="flex flex-wrap gap-2">
                          <span className="text-xs px-2 py-1 bg-[#0a1628] text-white">
                            {ref.client}
                          </span>
                          <span className="text-xs px-2 py-1 bg-slate-200 text-slate-600">
                            {ref.duration}
                          </span>
                        </div>
                      </div>

                      {/* Reference Content */}
                      <div className="p-6">
                        <p className="body-text text-sm mb-4">
                          <strong>Contexte :</strong> {ref.context}
                        </p>
                        <div>
                          <p className="text-xs uppercase tracking-wider text-slate-400 mb-3">Livrables clés</p>
                          <ul className="space-y-2">
                            {ref.deliverables.map((deliverable, dIdx) => (
                              <li key={dIdx} className="flex items-start text-sm text-slate-600">
                                <span className="w-1.5 h-1.5 rounded-full bg-[#BFA15F] mr-2 mt-2 flex-shrink-0" />
                                {deliverable}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          
          <p className="text-center text-sm text-slate-500 mt-16 max-w-2xl mx-auto">
            Toutes les références peuvent être discutées en détail dans un cadre de confidentialité approprié. 
            Pour toute information complémentaire sur un secteur ou un domaine d'expertise spécifique, 
            n'hésitez pas à prendre contact directement.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#0a1628] text-white">
        <div className="container-main text-center">
          <h2 className="text-3xl md:text-4xl font-bold font-heading mb-6">
            Un projet similaire ?
          </h2>
          <p className="text-slate-300 text-lg max-w-xl mx-auto mb-10">
            Discutons de vos enjeux et de la meilleure façon dont 
            je peux vous accompagner dans votre transformation.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a 
              href="mailto:yoann.cohen@wyco.fr" 
              data-testid="references-contact-btn"
              className="btn-gold"
            >
              Contactez-moi
              <ArrowRight size={16} className="ml-2" />
            </a>
            <Link to="/seminaire" data-testid="references-seminaire-btn" className="btn-secondary border-white/30 text-white hover:bg-white hover:text-[#0a1628]">
              Séminaire de transformation
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ReferencesPage;
